import { Alert, Box } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useState } from "react";
import {
  columns as baseColumns,
  createCompleteTaskColumn,
  createParametersColumn,
  statusColumn,
} from "./grid-data";
import { LongRunningTaskModel } from "./types";
import TaskParametersDialog from "./task-parameters-dialog";
import CompleteLongRunningTaskDialog from "./complete-long-running-task-dialog";
import { setTaskAsCompleted } from "./task-info-service";
import { useNotification } from "../../context/notification-provider";
import {
  getCompleteTaskErrorMessage,
  getCompleteTaskSuccessMessage,
} from "./task-completion-messages";

const TaskInfoGrid = ({
  data,
  isLoading,
  error,
  showLongRunningColumns = false,
  onComplete,
}: {
  data: LongRunningTaskModel[] | undefined;
  isLoading: boolean;
  error: unknown;
  showLongRunningColumns?: boolean;
  onComplete?: () => Promise<void>;
}) => {
  const { showNotification } = useNotification();
  const [selectedTask, setSelectedTask] = useState<LongRunningTaskModel | null>(
    null,
  );
  const [taskToComplete, setTaskToComplete] =
    useState<LongRunningTaskModel | null>(null);
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);

  const handleOpenDialog = (task: LongRunningTaskModel) => {
    if (task.task.jsonParameters) {
      setSelectedTask(task);
    }
  };

  const handleCloseDialog = () => {
    setSelectedTask(null);
  };

  const handleOpenCompleteDialog = (task: LongRunningTaskModel) => {
    setTaskToComplete(task);
  };

  const handleCloseCompleteDialog = () => {
    setTaskToComplete(null);
  };

  const handleCompleteTask = async () => {
    const task = taskToComplete;

    if (!task) return;

    setCompletingTaskId(task.task.id);
    try {
      const response = await setTaskAsCompleted({
        id: task.task.id,
        tableId: task.tableInformation.id,
      });
      showNotification({
        message: getCompleteTaskSuccessMessage(response),
        severity: "success",
        duration: 3000,
      });
      await onComplete?.();
    } catch (completeError) {
      showNotification({
        message: getCompleteTaskErrorMessage(completeError),
        severity: "error",
        duration: 3000,
      });
    } finally {
      setCompletingTaskId(null);
      setTaskToComplete(null);
    }
  };

  const columns = [
    baseColumns[0], // Task ID
    baseColumns[1], // Description
    createParametersColumn(handleOpenDialog),
    baseColumns[2], // Date Requested
    ...(showLongRunningColumns ? [baseColumns[3], baseColumns[4]] : []),
    baseColumns[5], // Run Time
    statusColumn,
    createCompleteTaskColumn(handleOpenCompleteDialog, completingTaskId),
  ];

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {Boolean(error) && (
        <Alert severity="error">Unable to load task information.</Alert>
      )}

      {!error && (
        <>
          <DataGrid
            rows={data ?? []}
            getRowId={(row) => row.task.id}
            columns={columns}
            loading={isLoading}
            pagination
            initialState={{
              pagination: { paginationModel: { pageSize: 20 } },
            }}
            pageSizeOptions={[20, 40, 60, 100]}
            localeText={{
              noRowsLabel:
                "No data available. Please adjust filters or try again.",
            }}
            sx={{
              height: "100%",
              border: "1px solid",
              borderColor: "grey.200",
              borderRadius: 3,
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              overflow: "hidden",
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid",
                borderColor: "grey.100",
                fontSize: "0.875rem",
                padding: "12px 10px",
              },
              "& .MuiDataGrid-columnHeaders": {
                background: "linear-gradient(to bottom, #f8f9fa, #f0f1f3)",
                borderBottom: "2px solid",
                borderColor: "grey.300",
                fontSize: "0.875rem",
                fontWeight: 700,
                color: "text.primary",
              },
              "& .MuiDataGrid-columnHeader": {
                padding: "12px 10px",
              },
              "& .MuiDataGrid-row": {
                transition: "all 0.15s ease-in-out",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                  cursor: "pointer",
                  transform: "scale(1.001)",
                },
                "&.Mui-selected": {
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(25, 118, 210, 0.12)",
                  },
                },
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "2px solid",
                borderColor: "grey.300",
                background: "linear-gradient(to bottom, #f8f9fa, #ffffff)",
                overflow: "hidden",
                minHeight: 56,
              },
              "& .MuiDataGrid-virtualScroller": {
                backgroundColor: "background.paper",
              },
            }}
            slotProps={{
              loadingOverlay: {
                variant: "circular-progress",
                noRowsVariant: "circular-progress",
              },
              baseIconButton: {
                size: "small",
              },
            }}
          />
          <TaskParametersDialog
            task={selectedTask?.task || null}
            onClose={handleCloseDialog}
          />
          <CompleteLongRunningTaskDialog
            task={taskToComplete}
            isCompleting={Boolean(completingTaskId)}
            title="Complete task?"
            onClose={handleCloseCompleteDialog}
            onComplete={handleCompleteTask}
          />
        </>
      )}
    </Box>
  );
};

export default TaskInfoGrid;
